import { useEffect, useState } from 'react'
import { useLocation, useNavigate, type NavigateFunction } from 'react-router'
import { redirectTo } from '@climate/components'
import type { LoginResponse } from './types'

// Only follow a post-login redirect back to one of our own apps: same host, a
// localhost port (dev), or a *.tellusmarkets.com subdomain. Anything else (an
// absolute external URL, //evil.com, javascript: scheme) is dropped so the login
// page can't be used as an open-redirect / phishing pivot.
export function safeRedirectTarget(raw: string | null): string | null {
  // Reject empty values and links already in the wild that carry the literal
  // "null"/"undefined" (they used to land the user on /app/null).
  if (!raw || raw === 'null' || raw === 'undefined') return null
  try {
    const url = new URL(raw, window.location.origin)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    const here = window.location.hostname
    if (url.hostname === here) return url.href
    if (url.hostname === 'localhost' && here === 'localhost') return url.href
    const registrable = here.split('.').slice(-2).join('.')
    if (registrable.includes('.') && url.hostname.endsWith(`.${registrable}`)) return url.href
    return null
  } catch {
    return null
  }
}

/**
 * Where a finished sign-in lands.
 *
 * Shared by all three screens that can finish one: the password alone where no
 * second factor is owed, the code challenge, and the enrolment that ends in a
 * session. Written once because "which app asked for this login" is carried in
 * a query string that is easy to drop, and dropping it is how a member who
 * clicked a link in an email ends up staring at the wrong app's home page.
 */
export function landAfterSignIn(
  session: LoginResponse,
  search: string,
  navigate: NavigateFunction
) {
  const redirect = safeRedirectTarget(new URLSearchParams(search).get('redirect_url'))

  if (session.entity_status !== 'active') { // stay at the onboarding screen if inactive
    navigate('/app/onboard', { replace: true })
  } else if (redirect) { // go back if another app requested login
    redirectTo(redirect)
  } else { // otherwise go to the home screen
    navigate('/app/home', { replace: true })
  }
}

/** What the login screen hands the second-factor screens. */
export interface MfaHandover {
  mfaToken: string
  /** The login URL's query string, so the redirect survives the extra step. */
  search: string
}

/**
 * The handover, read once and then wiped from the browser history entry.
 *
 * Router state lives in that entry, and browsers keep it across a reload and
 * Back/Forward. Left there, the code screen would come back without the
 * password step it belongs to. Kept in this screen's memory instead, so
 * leaving or reloading the screen ends the handover and lands on the login.
 */
export function useMfaHandover(): MfaHandover | null {
  const location = useLocation()
  const navigate = useNavigate()
  const [handover] = useState(() => location.state as MfaHandover | null)

  useEffect(() => {
    if (location.state) navigate(location, { replace: true, state: null })
  }, [location, navigate])

  return handover
}
